import { defaultDataIdFromObject } from 'apollo-cache-inmemory';

// TODO add operations to intercept as we migrate those queries to relay
const OPERATIONS_TO_INTERCEPT: string[] = [];

/**
 * Matches the nested Content object shape:
 * `__typename`, `id`, and `version` (plus `__apolloOperationName` injected by the cache).
 *
 * ```
 *  object: {
 *		__typename: 'Content',
 *		version: {
 * 			__typename: 'Version',
 *			number: 1,
 *		},
 *		id: '1',
 *	}
 * ```
 */
function isContentObjectShape(object: Record<string, unknown>): boolean {
	const contentObjectShapeKeys = new Set(['__typename', 'id', 'version']);
	const versionKeys = new Set(['__typename', 'number']);

	if (object.__typename !== 'Content' || typeof object.id !== 'string') {
		return false;
	}
	const version = object.version;
	if (version === null || typeof version !== 'object') {
		return false;
	}

	const versionRecord = version as Record<string, unknown>;
	const versionKeyList = Object.keys(versionRecord);
	if (
		versionKeyList.length !== versionKeys.size ||
		!versionKeyList.every((key) => versionKeys.has(key)) ||
		versionRecord.__typename !== 'Version' ||
		typeof versionRecord.number !== 'number'
	) {
		return false;
	}

	const keys = Object.keys(object).filter((key) => key !== '__apolloOperationName');
	if (keys.length !== contentObjectShapeKeys.size) {
		return false;
	}
	return keys.every((key) => contentObjectShapeKeys.has(key));
}

/**
 * Like {@link dataIdFromObject} but uses the given operation names for the nested-`Content` intercept.
 */
export function dataIdFromObjectWithOperationInterceptList(
	object: any,
	interceptOperationNames: readonly string[],
) {
	const operationName = object.__apolloOperationName;

	if (interceptOperationNames.includes(operationName)) {
		// don't return ids for specific intercepted operations to prevent apollo from caching
		if (isContentObjectShape(object)) {
			return;
		}
	}

	if (object.__typename === 'WebItem') {
		return `${object.completeKey || object.id}; ${object.url}`;
	} else if (object.__typename === 'OnboardingState') {
		return object.key;
	} else if (object.__typename === 'WebSection') {
		return object.cacheKey || object.id;
	} else if (object.__typename === 'DetailsSummaryLines') {
		return `${object.macroId}_${object.currentPage}`;
	} else if (object.__typename === 'DetailLine') {
		return `${object.id}_${object.rowId}_${object.macroId}`;
	} else if (object.__typename === 'ConfluenceSystemTemplate') {
		return object.moduleCompleteKey;
	} else if (object.id === null) {
		return;
	}

	return defaultDataIdFromObject(object);
}

export const dataIdFromObject = (object: any) =>
	dataIdFromObjectWithOperationInterceptList(object, OPERATIONS_TO_INTERCEPT);
